import { Inject, Injectable } from "@angular/core";
import { CacheFactory, RequestPayload, StorageMode } from "@obg/ngx.global";
import { LoggingFactory } from "@obg/ngx.global.evo";
import { compact, forEach, merge } from "lodash-es";
import { combineLatest, Observable, of } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";

import { STARTUP_CONTEXT_TOKEN } from "../app-context/app-context.const";
import { StartupContext } from "../app-context/app-context.model";
import { SESSION_TOKEN_HEADER } from "../auth/session.const";
import { CoreConfig } from "../config/core-config";
import { CoreHttpClient } from "../http-client/http-client.service";

import { PageConfigService } from "./page.config";
import { Page, PageContentResponse, PageContentSegment, PageData, PageRoute, PageSegmentData } from "./page.model";
import { getDocumentKey } from "./page.util";

const CACHE_STORE_KEY = "core.page";
const PAGE_CACHE_KEY = "page";

@Injectable({
	providedIn: "root",
})
export class PageService {
	private cache = this.cacheFactory.get(CACHE_STORE_KEY, {
		storageMode: StorageMode.Memory,
		maxAge: this.config.getPagesCacheMaxAge(),
	});
	private logger = this.loggingFactory.get("pageService");

	constructor(
		private httpClient: CoreHttpClient,
		private config: PageConfigService,
		private loggingFactory: LoggingFactory,
		private cacheFactory: CacheFactory,
		private coreConfig: CoreConfig,
		@Inject(STARTUP_CONTEXT_TOKEN) private startupContext: StartupContext
	) {}

	getPage({ segment, parentSegments }: PageSegmentData): Observable<PageData> {
		const pageContentResponse: PageContentResponse = { metaContent: {}, translatedRoutes: {} };
		return combineLatest(
			[segment, ...parentSegments].map((segmentItem) => {
				const documentKey = getDocumentKey(segmentItem);
				return this.cache
					.getItemAsObservable<{
						metaContent: Page;
						translatedRoutes: PageRoute[];
					}>(`${PAGE_CACHE_KEY}:${documentKey}`)
					.pipe(
						map((cachedSegment) => {
							if (cachedSegment) {
								pageContentResponse.metaContent[documentKey] = cachedSegment.metaContent;
								pageContentResponse.translatedRoutes[documentKey] = cachedSegment.translatedRoutes;
								return undefined;
							}

							return segmentItem;
						})
					);
			})
		).pipe(
			map(compact),
			switchMap((uncachedSegments) =>
				uncachedSegments.length
					? this.getContentResponse$(uncachedSegments, { segment, parentSegments }, pageContentResponse)
					: of(this.transformPageData(segment, pageContentResponse, parentSegments))
			)
		);
	}

	private getContentResponse$(
		uncachedSegments: PageContentSegment[],
		{ segment, parentSegments }: PageSegmentData,
		pageContentResponse: PageContentResponse
	): Observable<PageData> {
		const baseRequestPayload: RequestPayload = {
			url: this.config.getPageEndpoint(),
			searchParams: {
				segments: uncachedSegments.map(getDocumentKey).sort().join(","),
				includeTranslatedRoutes: true,
			},
		};

		const mainResponse$ = this.httpClient.get<PageContentResponse>(baseRequestPayload);
		const alternateResponses$ = this.coreConfig.alternateDomains
			?.filter((alternateDomain) => alternateDomain.brandId && alternateDomain.languageCodes.length)
			.map((alternateDomain) =>
				this.httpClient.get<PageContentResponse>({
					...baseRequestPayload,
					headers: {
						brandId: alternateDomain.brandId!,
						marketCode: alternateDomain.languageCodes[0],
						[SESSION_TOKEN_HEADER]: "",
					},
				})
			);

		return combineLatest([mainResponse$, ...(alternateResponses$ ?? [])]).pipe(
			tap((contentResponses) => {
				forEach(uncachedSegments, (segmentItem) => {
					const documentKey = getDocumentKey(segmentItem);
					const contentResponsePart: PageContentResponse = {
						metaContent: {
							[documentKey]: contentResponses[0].metaContent[documentKey],
						},
						translatedRoutes: {
							[documentKey]: this.mergeTranslatedRoutes(contentResponses, documentKey),
						},
					};

					this.cache.setItemAsObservable(`${PAGE_CACHE_KEY}:${documentKey}`, {
						metaContent: contentResponsePart.metaContent[documentKey],
						translatedRoutes: contentResponsePart.translatedRoutes[documentKey],
					});

					pageContentResponse = merge(pageContentResponse, contentResponsePart);
				});
			}),
			map(() => this.transformPageData(segment, pageContentResponse, parentSegments))
		);
	}

	private transformPageData(
		segment: PageContentSegment,
		pageContentResponse: PageContentResponse,
		parentSegments: PageContentSegment[]
	): PageData {
		return {
			page: this.processRawPage(pageContentResponse, segment, parentSegments),
			parentPages: parentSegments.map((parentSegment) => this.processRawPage(pageContentResponse, parentSegment)),
		};
	}

	private processRawPage(
		rawResponse: PageContentResponse,
		segment: PageContentSegment,
		parentSegments: PageContentSegment[] = []
	): Page {
		const key = getDocumentKey(segment);
		let translatedRoutes = Object.values(rawResponse.translatedRoutes[key] || {});
		const crsSegments: Partial<Page> = {};
		const rawPageItem = rawResponse.metaContent[key];
		if (rawPageItem) {
			translatedRoutes = translatedRoutes.map((translatedRoute) => ({
				...translatedRoute,
				languageCode: this.overrideLanguageCode(translatedRoute.languageCode),
				path: translatedRoute.path || rawPageItem.slug,
			}));

			let pageKey: keyof Page;
			for (pageKey in rawPageItem) {
				if (rawPageItem.hasOwnProperty(pageKey) && pageKey && typeof rawPageItem[pageKey] === "string") {
					crsSegments[pageKey] = unescape(rawPageItem[pageKey] as keyof Page) || ("" as any);
				}
			}
		} else {
			this.logger.warn("processRawPage", `Page item missing for key: ${key}.`);
		}

		return {
			...crsSegments,
			documentKey: segment.key,
			translatedRoutes,
			parentSegments,
		} as Page;
	}

	private overrideLanguageCode(languageCode: string): string {
		const matchingMarket = this.startupContext.marketList.find(
			(market) => market.urlSlug && market.languageCode === languageCode
		);
		return matchingMarket?.urlSlug ?? languageCode;
	}

	private mergeTranslatedRoutes(contentResponses: PageContentResponse[], documentKey: string): PageRoute[] {
		return contentResponses.reduce((routes, response) => {
			for (const route of response.translatedRoutes[documentKey] ?? []) {
				if (!routes.some((r) => r.languageCode === route.languageCode)) {
					routes.push(route);
				}
			}

			return routes;
		}, [] as PageRoute[]);
	}
}
